import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../lib/api';
import { BedDouble, Star, AlertTriangle, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';

export const RoomFeedbackResults = ({ organizationId }) => {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const params = {};
        if (organizationId && organizationId !== 'all') params.organization_id = organizationId;
        setLoading(true);
        api.get('/hotel/admin/room-feedback', { params })
            .then(res => setData(res.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [organizationId]);

    const formatDate = (ts) => {
        if (!ts) return '-';
        return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    if (loading) {
        return <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>;
    }
    if (!data) return null;

    const { stats, responses } = data;

    return (
        <Card data-testid="room-feedback-results">
            <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                    <BedDouble className="w-4 h-4 text-teal-500" />
                    {t('hotel.rfResultsTitle', 'Welcome / Room')}
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                {/* Stats */}
                <div className="grid grid-cols-3 gap-4">
                    <div className="p-3 rounded-lg border bg-card" data-testid="rf-stat-total">
                        <p className="text-xs text-muted-foreground">{t('hotel.rfTotalResponses', 'Total Responses')}</p>
                        <p className="text-xl font-bold">{stats.total_responses}</p>
                    </div>
                    <div className="p-3 rounded-lg border bg-card" data-testid="rf-stat-avg">
                        <p className="text-xs text-muted-foreground">{t('hotel.rfAvgRating', 'Avg. Rating')}</p>
                        <p className="text-xl font-bold">{stats.avg_rating}<span className="text-xs text-muted-foreground"> /5</span></p>
                    </div>
                    <div className="p-3 rounded-lg border bg-card" data-testid="rf-stat-alerts">
                        <p className="text-xs text-muted-foreground flex items-center gap-1"><AlertTriangle className="w-3 h-3 text-red-500" />{t('hotel.rfLowAlerts', 'Low-Rating Alerts')}</p>
                        <p className="text-xl font-bold text-red-500">{stats.low_rating_alerts}</p>
                    </div>
                </div>

                {/* Per-question averages */}
                {stats.per_question?.length > 0 && (
                    <div className="space-y-1.5" data-testid="rf-per-question">
                        {stats.per_question.map((pq, i) => (
                            <div key={i} className="flex items-center justify-between text-sm border-b border-dashed pb-1.5 last:border-0">
                                <span className="text-muted-foreground truncate pr-3">{pq.question}</span>
                                <span className="flex items-center gap-1 font-semibold whitespace-nowrap">
                                    <Star className="w-3.5 h-3.5" fill="#FBBF24" stroke="#FBBF24" />{pq.avg}
                                    <span className="text-xs text-muted-foreground font-normal">({pq.count})</span>
                                </span>
                            </div>
                        ))}
                    </div>
                )}

                {/* Responses table */}
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>{t('common.date', 'Date')}</TableHead>
                                <TableHead>{t('hotel.rfRoom', 'Room')}</TableHead>
                                <TableHead>{t('common.location', 'Location')}</TableHead>
                                <TableHead>{t('hotel.rfRatings', 'Ratings')}</TableHead>
                                <TableHead>{t('hotel.rfComment', 'Comment')}</TableHead>
                                <TableHead>{t('hotel.rfAlert', 'Alert')}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {responses.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} className="text-center py-6 text-muted-foreground">
                                        {t('hotel.rfNoResponses', 'No Room Feedback responses yet')}
                                    </TableCell>
                                </TableRow>
                            ) : responses.map(r => (
                                <TableRow key={r.id} data-testid={`rf-response-row-${r.id}`}>
                                    <TableCell className="text-xs whitespace-nowrap">{formatDate(r.created_at)}</TableCell>
                                    <TableCell className="text-sm font-medium">{r.room_number || '-'}</TableCell>
                                    <TableCell className="text-sm">{r.location_name || '-'}</TableCell>
                                    <TableCell>
                                        <div className="space-y-0.5">
                                            {(r.answers || []).map((a, i) => (
                                                <div key={i} className="flex items-center gap-1.5 text-xs">
                                                    <span className={`font-semibold ${a.rating <= (r.threshold ?? 4) ? 'text-red-500' : 'text-emerald-500'}`}>{a.rating}★</span>
                                                    <span className="text-muted-foreground truncate max-w-[240px]">{a.question_text}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-xs text-muted-foreground max-w-[200px]">
                                        <span className="line-clamp-2">{r.comment || '-'}</span>
                                    </TableCell>
                                    <TableCell>
                                        {r.low_count > 0 ? (
                                            <div className="flex flex-col gap-1">
                                                <Badge variant="outline" className={`text-xs ${r.alert_sms_fired ? 'bg-red-500/10 text-red-500 border-red-500/30' : 'bg-slate-500/10 text-slate-400'}`}>
                                                    SMS {r.alert_sms_fired ? t('hotel.sent', 'Sent') : t('manager.off', 'Off')}
                                                </Badge>
                                                <Badge variant="outline" className={`text-xs ${r.alert_email_sent ? 'bg-blue-500/10 text-blue-400 border-blue-500/30' : 'bg-slate-500/10 text-slate-400'}`}>
                                                    Email {r.alert_email_sent ? t('hotel.sent', 'Sent') : t('manager.off', 'Off')}
                                                </Badge>
                                            </div>
                                        ) : (
                                            <Badge variant="outline" className="text-xs bg-emerald-500/10 text-emerald-500 border-emerald-500/30">OK</Badge>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </CardContent>
        </Card>
    );
};
